import { Alert, Box, Chip, Stack, Typography } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * Which apps could read this record, and why (LO-76).
 *
 * The product's argument, applied to one row. `manifest ∩ user` is stated as
 * a principle on every screen; this is the same rule made checkable against
 * something real, which is the form an operator can actually act on.
 *
 * **It is the app half only, and the heading says so.** An app listed here
 * means its manifest permits this record — not that a given person using it
 * can see the record, which additionally needs that person's groups. "Three
 * apps can read this" when the truth is "three apps could, for a user who
 * holds the tags" is precisely the overstatement this product exists to
 * avoid, and it would be a bad place to start making it.
 *
 * The verdicts come from the kernel rather than being recomputed here. The
 * rule — a record's tags must be a subset of a grant's — is authorization
 * logic, and a second copy of it in a component is a copy that will one day
 * disagree with the one that enforces.
 */

const REASONS = {
  tags_declared: { label: "declares these tags" },
  untagged: { label: "record is untagged" },
  tag_not_declared: { label: "tag not declared" },
  type_not_declared: { label: "type not declared" },
  // A record one deployment wrote. The grant is per app type and the record
  // is not, so the type declaring it is necessary and not sufficient.
  instance_scoped: { label: "this instance only" },
  other_instance_scope: { label: "another instance owns it" },
};

export default function ReachableBy({ apps, tags }) {
  if (!apps?.length) return null;

  const permitted = apps.filter((a) => a.can_read);

  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        Which apps could reach this record
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Apps only. An app listed here is permitted this record by its
        manifest; whether a particular person sees it through that app also
        depends on their groups.
      </Typography>

      {!tags?.length ? (
        // The rule everybody reads backwards, said before they draw the wrong
        // conclusion from a long list.
        <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
          This record carries no tags. Tags restrict, so an untagged record is
          the <strong>widest</strong> case — every app that declares the type
          at all can reach it. That is not a misconfiguration, and it is the
          part people read the wrong way round.
        </Alert>
      ) : null}

      <Stack spacing={1}>
        {apps.map((app) => {
          const meaning = REASONS[app.reason] || { label: app.reason };
          return (
            <Stack
              key={app.app}
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              {/* A word, not only a colour — a permission rendered as a
                  green dot is a permission a colour-blind operator cannot
                  read at all. */}
              <Chip
                size="small"
                variant="outlined"
                label={app.can_read ? "can read" : "cannot"}
                sx={{
                  color: app.can_read ? "kernel.permitted" : "kernel.stale",
                  borderColor: app.can_read ? "kernel.permitted" : "kernel.stale",
                  minWidth: 84,
                }}
              />
              <Tooltip title={app.detail || ""}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {app.name || app.app}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {meaning.label}
                {app.owner ? ` · ${app.owner}` : ""}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {permitted.length} of {apps.length}{" "}
        {apps.length === 1 ? "app" : "apps"} may reach it.
      </Typography>
    </Box>
  );
}
